import React from "react";
import { useDispatch } from "react-redux";
import { Box, Typography, Button, Divider, TextField } from "@mui/material";

import {
  ClientLinkRequest,
  ClientLinkRequestStatus,
  ClientLinkRequestStatusType
} from "../../interfaces/ClientLinkRequests";
import { notificationAdd } from "../../actions/notification";
import { deleteClientLinkRequest, patchClientLinkRequest } from "../../api/clientLink";
import { ClientLinkRequestStatusChip } from "./ClientLinkRequest/ClientLinkRequestStatusChip";

// Helper function to format time ago
const timeAgo = (dateString: string) => {
  const now = new Date();
  const past = new Date(dateString);
  const diffMs = now.getTime() - past.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return `${diffSec} sec ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} hr ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD} day${diffD > 1 ? "s" : ""} ago`;
};

// eslint-disable-next-line import/prefer-default-export
export function ClientLinkRequests({
  sentClientLinkRequests,
  setSentClientLinkRequests,
  receivedClientLinkRequests,
  setReceivedClientLinkRequests
}: {
  sentClientLinkRequests: Array<ClientLinkRequest>;
  setSentClientLinkRequests: React.Dispatch<React.SetStateAction<Array<ClientLinkRequest>>>;
  receivedClientLinkRequests: Array<ClientLinkRequest>;
  setReceivedClientLinkRequests: React.Dispatch<React.SetStateAction<Array<ClientLinkRequest>>>;
}): React.ReactNode {
  const dispatch = useDispatch();

  const [remarks, setRemarks] = React.useState<Record<number, string>>({});

  // actions
  const updateReceivedClientLinkRequest = async ({
    requestId,
    status,
    hide,
    remark
  }: {
    requestId: number;
    status?: ClientLinkRequestStatusType;
    hide?: boolean;
    remark?: string;
  }) => {
    if (status === ClientLinkRequestStatus.REJECTED && !remark) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "warning",
          autoTimeout: true,
          message: "Remark is compulsory for rejection."
        })
      );

      return;
    }

    try {
      const { success } = await patchClientLinkRequest({ requestId, status, hide, remark });
      if (success) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "success",
            autoTimeout: true,
            message: "Updated the client link request successflly!"
          })
        );
        setReceivedClientLinkRequests((prev) => prev.filter((req) => req.id !== requestId));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          autoTimeout: true,
          message: err?.message || err?.data?.message || "Something went wrong"
        })
      );
    }
  };

  const hideSentClientLinkRequest = async ({
    requestId,
    hide
  }: {
    requestId: number;
    hide?: boolean;
  }) => {
    try {
      const { success } = await patchClientLinkRequest({ requestId, hide });
      if (success) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "success",
            autoTimeout: true,
            message: "Updated the client link request successflly!"
          })
        );
        setReceivedClientLinkRequests((prev) => prev.filter((req) => req.id !== requestId));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          autoTimeout: true,
          message: err?.message || err?.data?.message || "Something went wrong"
        })
      );
    }
  };

  const deleteSentClientLinkRequest = async ({ requestId }: { requestId: number }) => {
    try {
      const { success } = await deleteClientLinkRequest({ requestId });
      if (success) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "success",
            autoTimeout: true,
            message: "Updated the client link request successflly!"
          })
        );
        setSentClientLinkRequests((prev) => prev.filter((req) => req.id !== requestId));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          autoTimeout: true,
          message: err?.message || err?.data?.message || "Something went wrong"
        })
      );
    }
  };

  return (
    <Box sx={{ maxWidth: "500px" }}>
      {/* Requests received  */}
      {receivedClientLinkRequests.length > 0 && (
        <Box>
          <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 700, flex: 1 }}>
              Client Access Requests Received
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {receivedClientLinkRequests.length} Total
            </Typography>
          </Box>

          <Divider sx={{ my: 0 }} />

          <Box
            sx={{
              p: 2,
              maxHeight: 300,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            {receivedClientLinkRequests.map((req) => (
              <Box
                key={req.id}
                sx={{
                  background: "#F9FAFB",
                  borderRadius: 2,
                  p: 2,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  border: "1px solid #E3E8EF"
                }}
              >
                {/* Description */}
                <Typography sx={{ fontSize: 15, mb: 2 }}>
                  {req.requestingResourceCentre.name} is requesting to access data of client{" "}
                  {req.client.firstName} {req.client.lastName} ({timeAgo(req.created_at)})
                </Typography>

                {/* Actions */}
                {req.status === ClientLinkRequestStatus.PENDING && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 1
                    }}
                  >
                    <TextField
                      variant="outlined"
                      type="text"
                      placeholder="remark"
                      value={remarks[req.id] || ""}
                      onChange={(e) =>
                        setRemarks((prev) => ({
                          ...prev,
                          [req.id]: e.target.value
                        }))
                      }
                      sx={{ flex: 1 }}
                      slotProps={{
                        input: {
                          sx: {
                            height: 32
                          }
                        }
                      }}
                    />

                    <Button
                      variant="contained"
                      color="inherit"
                      sx={{
                        height: 30,
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 1,
                        bgcolor: "grey.400",
                        "&:hover": { bgcolor: "grey.500" }
                      }}
                      onClick={() =>
                        updateReceivedClientLinkRequest({
                          requestId: req.id,
                          status: ClientLinkRequestStatus.REJECTED,
                          remark: remarks[req.id]
                        })
                      }
                    >
                      Reject
                    </Button>

                    <Button
                      variant="contained"
                      color="success"
                      sx={{
                        height: 30,
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 1
                      }}
                      onClick={() =>
                        updateReceivedClientLinkRequest({
                          requestId: req.id,
                          status: ClientLinkRequestStatus.APPROVED,
                          remark: remarks[req.id]
                        })
                      }
                    >
                      Approve
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Requests sent */}
      {sentClientLinkRequests.length > 0 && (
        <Box>
          <Divider sx={{ my: 0 }} />
          <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 700, flex: 1 }}>Client Access Requests Sent</Typography>
            <Typography variant="body2" color="text.secondary">
              {sentClientLinkRequests.length} Total
            </Typography>
          </Box>
          <Divider sx={{ my: 0 }} />

          <Box
            sx={{
              p: 2,
              maxHeight: 300,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            {sentClientLinkRequests.map((req) => (
              <Box
                key={req.id}
                sx={{
                  background: "#F9FAFB",
                  borderRadius: 2,
                  p: 2,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  border: "1px solid #E3E8EF"
                }}
              >
                {/* Description */}
                <Box
                  sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <Typography sx={{ fontSize: 15, lineHeight: 1.4 }}>
                    You requested to access data of client {req.client.firstName}{" "}
                    {req.client.lastName} from {req.destinationResourceCentre.name} (
                    {timeAgo(req.created_at)})
                  </Typography>
                  <ClientLinkRequestStatusChip status={req.status} />
                </Box>

                {/* Actions */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  {req.remark && (
                    <Box
                      title={req.remark}
                      sx={{
                        height: 30,
                        px: 1,
                        flex: 1,
                        fontSize: 12,
                        border: "1px solid #E3E8EF",
                        borderRadius: 1,
                        bgcolor: "#F3F4F6",
                        overflow: "hidden",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {req.remark}
                      </span>
                    </Box>
                  )}

                  {req.status !== ClientLinkRequestStatus.PENDING && (
                    <Button
                      variant="contained"
                      color="inherit"
                      sx={{
                        height: 30,
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 1,
                        "&:hover": { bgcolor: "grey.500" }
                      }}
                      onClick={() =>
                        hideSentClientLinkRequest({
                          requestId: req.id,
                          hide: true
                        })
                      }
                    >
                      Hide
                    </Button>
                  )}

                  {req.status === ClientLinkRequestStatus.PENDING && (
                    <Button
                      variant="contained"
                      color="error"
                      sx={{
                        height: 30,
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 1
                      }}
                      onClick={() =>
                        deleteSentClientLinkRequest({
                          requestId: req.id
                        })
                      }
                    >
                      Delete
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
